import React, { useState, useRef } from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWD310Query } from './hooks/usePWD310Query';
import QueryParamsPanel from './components/QueryParams';
import ResultTable from './components/ResultTable';
import type { QueryParams } from '../../services/pwd310Api';
import './PWD310Form.css';

const PWD310Form: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const { loading, data, summary, queried, noData, runQuery, reset } = usePWD310Query();

  // Filter state
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');

  const handleQuery = () => {
    const params: QueryParams = {
      dateFrom: dateRange?.[0]?.format('YYYY-MM-DD') || '',
      dateTo: dateRange?.[1]?.format('YYYY-MM-DD') || '',
      vendorFrom: vendorFrom.trim() || undefined,
      vendorTo: vendorTo.trim() || undefined,
    };
    runQuery(params);
  };

  const handleReset = () => {
    setDateRange(null);
    setVendorFrom('');
    setVendorTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const now = new Date().toLocaleString('zh-TW');
    const dateLabel = dateRange
      ? `${dateRange[0]?.format('YYYY/MM/DD') || ''} ~ ${dateRange[1]?.format('YYYY/MM/DD') || ''}`
      : '';

    win.document.write(`<!DOCTYPE html><html><head><title>廠商對帳單</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 12px; }
  h2 { text-align: center; margin-bottom: 4px; }
  .meta { text-align: center; font-size: 11px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th, td { border: 1px solid #333; padding: 3px 6px; }
  th { background: #f0f0f0; text-align: center; }
  .right { text-align: right; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .section-title { font-weight: bold; background: #e8e8e8; }
  @media print { body { margin: 10mm; } }
</style></head><body>
<h2>廠商對帳單</h2>
<div class="meta">日期範圍: ${dateLabel}　　列印時間: ${now}　　共 ${summary?.vendor_count || 0} 家廠商</div>
<table>
<thead>
  <tr>
    <th>廠商代號</th><th>廠商名稱</th><th>幣別</th>
    <th style="text-align:right">進貨金額</th>
    <th style="text-align:right">退貨金額</th>
    <th style="text-align:right">付款金額</th>
    <th style="text-align:right">餘額</th>
  </tr>
</thead>
<tbody>
${data.map(r => `<tr>
  <td class="center">${r.vendor_code}</td>
  <td>${r.vendor_name}</td>
  <td class="center">${r.currency}</td>
  <td class="right">${r.receipt_total.toFixed(2)}</td>
  <td class="right">${r.return_total.toFixed(2)}</td>
  <td class="right">${r.payment_total.toFixed(2)}</td>
  <td class="right bold">${r.balance.toFixed(2)}</td>
</tr>`).join('')}
<tr class="section-title">
  <td colspan="3" class="center">合計</td>
  <td class="right">${summary?.total_receipt.toFixed(2) || '0.00'}</td>
  <td class="right">${summary?.total_return.toFixed(2) || '0.00'}</td>
  <td class="right">${summary?.total_payment.toFixed(2) || '0.00'}</td>
  <td class="right bold">${summary?.total_balance.toFixed(2) || '0.00'}</td>
</tr>
</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <PermissionGuard module={Modules.PWD310} action="query">
      <div className="pwd310-container" ref={printRef}>
        <div className="pwd310-header">
          <h2>廠商對帳單</h2>
          <div className="pwd310-print-btn">
            {queried && !noData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                列印
              </Button>
            )}
          </div>
        </div>

        <QueryParamsPanel
          dateRange={dateRange}
          vendorFrom={vendorFrom}
          vendorTo={vendorTo}
          loading={loading}
          onDateRangeChange={setDateRange}
          onVendorFromChange={setVendorFrom}
          onVendorToChange={setVendorTo}
          onQuery={handleQuery}
          onReset={handleReset}
        />

        <ResultTable
          data={data}
          summary={summary}
          loading={loading}
          queried={queried}
          noData={noData}
        />
      </div>
    </PermissionGuard>
  );
};

export default PWD310Form;
